/* eslint-disable react/no-unescaped-entities */
import React, { useState } from "react";
import { Mail, MessageCircle, MonitorSmartphone, MapPin, ListChecks } from "lucide-react";
import PageHero from "@/components/landing/PageHero";
import { PAGES } from "@/constants/testIds";
import { CONTACT_EMAIL, SUPPORT_EMAIL, WEB_REGISTER_URL } from "@/constants/site";
import PageMeta from "@/components/PageMeta";

const CONTACT_METHODS = [
  { icon: MessageCircle, title: "Product questions", value: CONTACT_EMAIL, action: `mailto:${CONTACT_EMAIL}`, testId: PAGES.contactWhatsappBtn, label: "Write to us" },
  { icon: Mail, title: "Customer support", value: SUPPORT_EMAIL, action: `mailto:${SUPPORT_EMAIL}`, testId: PAGES.contactEmailBtn, label: "Get support" },
  { icon: MonitorSmartphone, title: "Ready to explore?", value: "Create your Shopbook workspace", action: WEB_REGISTER_URL, testId: "contact-web-app-btn", label: "Start free" },
];

export default function Contact() {
  const params = new URLSearchParams(window.location.search);
  const topic = params.get("topic") || "";
  const platform = params.get("platform") || "";
  const isCustomerStory = topic === "customer-story";
  const initialMessage = topic === "mobile-beta"
    ? `I would like access to the Shopbook ${platform === "ios" ? "iPhone" : "Android"} beta.\n\nBusiness name:\nCountry:\nPrimary device:`
    : isCustomerStory
      ? "Business type:\nWhat was difficult before Shopbook:\nWhat changed after using Shopbook:\nA result we can verify:\nHow we may verify it:"
      : "";
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: initialMessage });
  const [storyConsent, setStoryConsent] = useState(false);
  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message || (isCustomerStory && !storyConsent)) return;
    const subjectLabel = topic === "mobile-beta" ? `${platform || "mobile"} beta request` : isCustomerStory ? "customer story submission" : "enquiry";
    const subject = encodeURIComponent(`Shopbook ${subjectLabel} from ${form.name}`);
    const consentLine = isCustomerStory ? "\n\nPublication consent: Granted for the submitted quotation/result, subject to Shopbook verifying the details and confirming final wording with me." : "";
    const body = encodeURIComponent(`Name: ${form.name}\nEmail: ${form.email}\nPhone: ${form.phone || "Not provided"}\n\n${form.message}${consentLine}`);
    window.location.href = `mailto:${CONTACT_EMAIL}?subject=${subject}&body=${body}`;
  };

  return (
    <>
      <PageMeta title="Contact" description="Bring Shopbook the real workflow, product, account, or billing question and give the team the context needed to help." />
      <PageHero
        eyebrow="Contact us"
        title="Bring us the real workflow."
        italicTail="Not a polished version."
        intro="Tell us what you were trying to do, what happened, and what the business needed instead. That is the fastest route to a useful answer."
      />

      {/* Methods */}
      <section className="bg-white py-16 md:py-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-12 grid grid-cols-1 md:grid-cols-3 gap-5">
          {CONTACT_METHODS.map((m) => (
            <article
              key={m.title}
              data-testid={m.testId}
              className="rounded-3xl bg-[#F7FAF9] p-7 border border-[#0E6B6B]/10 flex flex-col"
            >
              <div className="h-11 w-11 rounded-2xl bg-[#0E6B6B] text-white flex items-center justify-center mb-5">
                <m.icon className="h-5 w-5" strokeWidth={1.6} />
              </div>
              <h3 className="font-display text-lg font-semibold text-[#0A2424]">{m.title}</h3>
              <p className="mt-1 text-base text-[#1c2c2c]">{m.value}</p>
              <a
                href={m.action}
                target="_blank"
                rel="noreferrer"
                className="mt-5 inline-flex items-center font-semibold text-[#0E6B6B] hover:text-[#138282]"
              >
                {m.label} →
              </a>
            </article>
          ))}
        </div>
      </section>

      {/* Form + Info */}
      <section className="bg-[#F7FAF9] py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-6 lg:px-12 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <span className="text-xs tracking-[0.2em] uppercase font-bold text-[#D96C4A]">Send a message</span>
            <h2 className="mt-4 font-display text-3xl md:text-4xl font-semibold leading-[1.1] text-[#0A2424]">
              Tell us about your business.
            </h2>
            <p className="mt-5 text-base text-[#4B5E5E] leading-relaxed">
              Share a few details and your email app will open with a message addressed to our team. You stay in control of what gets sent.
            </p>

            <div className="mt-10 space-y-5 text-sm">
              <div className="flex items-start gap-3">
                <MapPin className="h-5 w-5 text-[#0E6B6B] mt-0.5" />
                <div>
                  <div className="font-semibold text-[#0A2424]">Head office</div>
                  <div className="text-[#4B5E5E]">Yaba, Lagos, Nigeria</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <ListChecks className="h-5 w-5 text-[#0E6B6B] mt-0.5" />
                <div>
                  <div className="font-semibold text-[#0A2424]">Helpful context</div>
                  <div className="text-[#4B5E5E]">Account email, branch, screen, action, and any error shown</div>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <form
              onSubmit={handleSubmit}
              className="rounded-3xl bg-white p-7 md:p-10 border border-[#0E6B6B]/10 shadow-[0_8px_30px_rgb(0,0,0,0.03)] space-y-5"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-semibold text-[#0A2424] mb-2">Full name *</label>
                  <input
                    name="name"
                    type="text"
                    value={form.name}
                    onChange={handleChange}
                    data-testid={PAGES.contactNameInput}
                    placeholder="Your name"
                    className="w-full rounded-xl border border-[#0E6B6B]/15 bg-[#F7FAF9] px-4 py-3 text-base text-[#0A2424] placeholder:text-[#4B5E5E]/60 focus:outline-none focus:border-[#0E6B6B] focus:ring-2 focus:ring-[#0E6B6B]/20"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-[#0A2424] mb-2">Email *</label>
                  <input
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={handleChange}
                    data-testid={PAGES.contactEmailInput}
                    placeholder="you@yourshop.com"
                    className="w-full rounded-xl border border-[#0E6B6B]/15 bg-[#F7FAF9] px-4 py-3 text-base text-[#0A2424] placeholder:text-[#4B5E5E]/60 focus:outline-none focus:border-[#0E6B6B] focus:ring-2 focus:ring-[#0E6B6B]/20"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-[#0A2424] mb-2">Phone (optional)</label>
                <input
                  name="phone"
                  type="tel"
                  value={form.phone}
                  onChange={handleChange}
                  data-testid={PAGES.contactPhoneInput}
                  placeholder="Your phone number"
                  className="w-full rounded-xl border border-[#0E6B6B]/15 bg-[#F7FAF9] px-4 py-3 text-base text-[#0A2424] placeholder:text-[#4B5E5E]/60 focus:outline-none focus:border-[#0E6B6B] focus:ring-2 focus:ring-[#0E6B6B]/20"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-[#0A2424] mb-2">How can we help? *</label>
                <textarea
                  name="message"
                  rows={5}
                  value={form.message}
                  onChange={handleChange}
                  data-testid={PAGES.contactMessageInput}
                  placeholder="What were you trying to do, what happened, and what did you expect instead?"
                  className="w-full rounded-xl border border-[#0E6B6B]/15 bg-[#F7FAF9] px-4 py-3 text-base text-[#0A2424] placeholder:text-[#4B5E5E]/60 focus:outline-none focus:border-[#0E6B6B] focus:ring-2 focus:ring-[#0E6B6B]/20 resize-none"
                />
              </div>

              {isCustomerStory ? (
                <label className="flex items-start gap-3 rounded-2xl border border-[#0E6B6B]/12 bg-[#F7FAF9] p-4 text-sm leading-relaxed text-[#3a4a4a]">
                  <input type="checkbox" checked={storyConsent} onChange={(event) => setStoryConsent(event.target.checked)} className="mt-1 h-4 w-4 accent-[#0E6B6B]" />
                  <span>I confirm this is my experience and allow Shopbook to verify it. Shopbook may publish the approved quotation or result only after confirming the final wording with me.</span>
                </label>
              ) : null}

              <button
                type="submit"
                disabled={!form.name || !form.email || !form.message || (isCustomerStory && !storyConsent)}
                data-testid={PAGES.contactSubmit}
                className="w-full sm:w-auto inline-flex items-center justify-center rounded-full bg-[#0E6B6B] px-7 py-3.5 text-base font-semibold text-white hover:bg-[#138282] disabled:opacity-60 transition-colors"
              >
                Open email to send
              </button>

              <p className="text-xs text-[#4B5E5E]">
                Nothing is sent automatically. Review the message in your email app before sending.
              </p>
            </form>
          </div>
        </div>
      </section>
    </>
  );
}
